import React, { useEffect, useState, useRef } from 'react';
import './Loader.scss';

interface LoaderProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fullScreen?: boolean;
  onAnimationComplete?: () => void;
  onClick?: () => void;
  isFadingOut?: boolean;
  showVideo?: boolean;
}

const Loader: React.FC<LoaderProps> = ({ 
  className = '', 
  size = 'md',
  fullScreen = false,
  onAnimationComplete,
  onClick,
  isFadingOut = false,
  showVideo = false
}) => {
  const [videoEnded, setVideoEnded] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const sizeClass = `loader--${size}`;
  const fullScreenClass = fullScreen ? 'loader--full-screen' : '';
  const clickableClass = onClick ? 'loader--clickable' : '';
  
  const fadeClass = isFadingOut ? 'loader--fade-out' : '';

  const classes = [
    'loader',
    sizeClass,
    fullScreenClass,
    clickableClass,
    fadeClass,
    className
  ].filter(Boolean).join(' ');

  // Forzar reproducción del video (necesario para iOS)
  useEffect(() => {
    if (showVideo && videoRef.current) {
      const playPromise = videoRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Video comenzó a reproducirse correctamente
            // Limpiar timeout si el video inició
            if (playTimeoutRef.current) {
              clearTimeout(playTimeoutRef.current);
            }
          })
          .catch(error => {
            // Autoplay fue bloqueado - común en iOS sin interacción o Low Power Mode
            setVideoFailed(true);
          });
      }
      
      // Timeout de seguridad: Si después de 2s no hay reproducción, considerar fallido
      // Esto detecta Low Power Mode de iOS donde el autoplay simplemente no funciona
      playTimeoutRef.current = setTimeout(() => {
        if (videoRef.current) {
          // Verificar si el video está realmente reproduciéndose
          if (videoRef.current.paused && videoRef.current.currentTime === 0) {
            
            setVideoFailed(true);
          }
        }
      }, 2000);
    }

    return () => {
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current);
      }
    };
  }, [showVideo]);

  useEffect(() => {
    // Cuando el video termina, llamar inmediatamente a onAnimationComplete
    if (showVideo && videoEnded && onAnimationComplete) {
      onAnimationComplete();
    }
  }, [showVideo, videoEnded, onAnimationComplete]);

  // Manejar video fallido - Skip automático después de 500ms
  useEffect(() => {
    if (videoFailed && onAnimationComplete) {
      
      const skipTimeout = setTimeout(() => {
        onAnimationComplete();
      }, 100);
      
      return () => clearTimeout(skipTimeout);
    }
  }, [videoFailed, onAnimationComplete]);

  const handleVideoEnded = () => {
    setVideoEnded(true);
  };

  return (
    <div className={classes}>
      <div className="loader__container">
        {showVideo ? (
          <video 
            ref={videoRef}
            className="loader__video"
            width="100%"
            height="100%"
            autoPlay
            muted
            playsInline
            preload="none"
            onEnded={handleVideoEnded}
            onLoadedData={() => {
              // Intentar reproducir cuando los datos estén cargados (iOS)
              if (videoRef.current) {
                videoRef.current.play().catch(err => {
                  
                  setVideoFailed(true);
                });
              }
            }}
            onPlay={() => {
              // Video logró reproducirse, limpiar estado de fallo
              setVideoFailed(false);
              if (playTimeoutRef.current) {
                clearTimeout(playTimeoutRef.current);
              }
            }}
          >
            <source src="/animaciones/loader.mp4" type="video/mp4" />
            Tu navegador no soporta la reproducción de video.
          </video>
        ) : (
          <div className="loader__spinner">
            <div className="loader__spinner-circle"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Loader;

